import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import "./ProfilePage.css";
import { AuthContext } from "../../components/AuthContext";
import { BASE_URL } from "../../config/baseUrl";

// Normalize user object
const normalizeUser = (user) => {
  if (!user) return {};

  return {
    id: user.userId || user.id || user._id || user.Id,
    name: user.fullName || user.name || user.FullName || "NA",
    email: user.email || user.Email || "NA",
    contactNo: user.mobileNo || user.MobileNo || user.contactNo || user.ContactNo || "NA",
    role: user.role || user.Role || user.userType || "User",
  };
};

const ProfilePage = () => {
  const { user } = useContext(AuthContext);

  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const normalizedUser = useMemo(() => normalizeUser(user), [user]);
  const userId = normalizedUser.id;

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const fetchEnrollments = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await axios.get(`${BASE_URL}/EnrollUser/user/${userId}`, {
            signal: controller.signal,
          });
          

        setEnrollments(res.data || []);
      } catch (err) {
        if (axios.isCancel(err)) return;

        console.error("Error fetching enrollment data:", err);

        const message =
          err.response?.data?.message ||
          err.message ||
          "Unable to load enrollments right now.";

        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollments();
    return () => controller.abort();
  }, [userId]);

  const groupByType = (type) =>
    enrollments.filter(
      (e) => e.catalogTypeName?.toLowerCase() === type.toLowerCase()
    );

  // Use phone from AuthContext or fallback to first enrollment
  const displayPhone =
    normalizedUser.contactNo !== "NA"
      ? normalizedUser.contactNo
      : enrollments[0]?.mobileNo || "NA";

  return (
    <div className="profile-page container">
      <h1 className="page-title">My Profile</h1>

      {/* ----------------- USER DETAILS ----------------- */}
      <div className="profile-card">
        <h2>Basic Details</h2>

        {!user ? (
          <p>Please sign in to view your profile details.</p>
        ) : (
          <>
            <p><b>Name:</b> {normalizedUser.name}</p>
            <p><b>Email:</b> {normalizedUser.email}</p>
            <p><b>Phone:</b> {displayPhone}</p>
            <p><b>Role:</b> {normalizedUser.role}</p>
            <p><b>User ID:</b> {normalizedUser.id || "NA"}</p>
          </>
        )}
      </div>

      {/* ----------------- ENROLLMENTS ----------------- */}
      <div className="enrolled-section">
        <h2>My Enrollments</h2>

        {!userId ? (
          <p>Sign in again to load your enrollments.</p>
        ) : loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p className="error-text">{error}</p>
        ) : enrollments.length === 0 ? (
          <p>No enrollments found.</p>
        ) : (
          <>
            {["Course", "Webinar", "Event", "Podcast"].map((type) => {
              const items = groupByType(type);
              if (!items.length) return null;

              const icon = {
                Course: "📘",
                Webinar: "🎤",
                Event: "🎯",
                Podcast: "🎧",
              }[type];

              return (
                <div className="enroll-box" key={type}>
                  <h3>{icon} {type}s</h3>
                  <ul>
                    {items.map((item) => (
                      <li key={item.enrollId}>
                        {item.catalogItem}
                        {item.enrollDate && (
                          <span className="date">
                            ({new Date(item.enrollDate).toLocaleDateString()})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
